/* eslint-disable @typescript-eslint/no-floating-promises */
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { usePopup } from "@nodeon/react-components";
import {
  addMinutes,
  endOfDay,
  format,
  isWithinInterval,
  startOfDay,
} from "date-fns";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Area, AreaChart } from "recharts";
import { BerthOrderListDto, useBerthApi } from "~/api/GeneratedApi";
import { useAppSelector } from "~/redux/store/store";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { BerthOrdersDayList } from "./BerthOrdersDayList";

interface Props {
  berthId: number;
  date: Date;
}

interface GraphData {
  name: string;
  value: number;
}

export const PortBerthOrderCard = ({ berthId, date }: Props): JSX.Element => {
  const berthApi = useBerthApi();
  const { t } = useTranslation("port-berth-orders");
  const [graphData, setGraphData] = React.useState<GraphData[]>([]);
  const { show } = usePopup();
  const isMobile = useAppSelector((s) => s.general.isMobile);

  const { data: orders } = useCustomQuery(
    { key: "berth-orders", id: berthId },
    () => berthApi.getBerthOrders(berthId),
    {
      select: (data) =>
        data.results.filter(
          (o) =>
            isWithinInterval(date, {
              start: o.startTimestamp,
              end: o.endTimestamp,
            }) ||
            isWithinInterval(o.startTimestamp, {
              start: startOfDay(date),
              end: endOfDay(date),
            }) ||
            isWithinInterval(o.endTimestamp, {
              start: startOfDay(date),
              end: endOfDay(date),
            }),
        ) ?? [],
    },
  );

  const { data: berth } = useCustomQuery({ key: "berth", id: berthId }, () =>
    berthApi.getBerth(berthId),
  );

  const findTimeblockOrder = useCallback(
    (time: Date): BerthOrderListDto | undefined =>
      (orders ?? []).find(
        (o) =>
          time.valueOf() >= o.startTimestamp.valueOf() &&
          time.valueOf() < o.endTimestamp.valueOf(),
      ),
    [orders],
  );

  React.useEffect(() => {
    const data = [];
    for (let i = 0; i < 48; i++) {
      const time = addMinutes(date, i * 30);
      const foundOrder = findTimeblockOrder(time);
      data.push({ value: foundOrder ? 1 : 0, name: format(time, "p") });
    }
    setGraphData(data);
  }, [date, findTimeblockOrder]);

  // Returns the amount of "used half hours" in a day
  const timeBlocksUsedInADay = useCallback(() => {
    let current = 0;
    for (let i = 0; i < 48; i++) {
      const time = addMinutes(date, i * 30);
      if (findTimeblockOrder(time)) current++;
    }
    return current;
  }, [date, findTimeblockOrder]);

  const scaleGreenToRed = useCallback((percentage: number): string => {
    let r;
    let g;
    const b = 0;
    if (percentage < 50) {
      r = 255;
      g = Math.round(5.1 * percentage);
    } else {
      g = 255;
      r = Math.round(510 - 5.1 * percentage);
    }
    const h = r * 0x10000 + g * 0x100 + b * 0x1;
    return `#${`000000${h.toString(16)}`.slice(-6)}55`;
  }, []);

  const usagePercentage = useMemo(
    () => Math.floor((timeBlocksUsedInADay() / 48) * 100),
    [timeBlocksUsedInADay],
  );

  const freeTimeLeft = useMemo(
    () =>
      `${Math.floor((48 - timeBlocksUsedInADay()) / 2)}${
        (48 - timeBlocksUsedInADay()) % 2 ? ":30" : ""
      }`,
    [timeBlocksUsedInADay],
  );

  const onClick = useCallback(() => {
    if (!orders) return;
    show(
      ({ close }) => (
        <>
          <DialogTitle>{berth?.name}</DialogTitle>
          <DialogContent sx={{ display: "flex", justifyContent: "center" }}>
            <BerthOrdersDayList
              berthId={berthId}
              date={date}
              firstColumn
              solo
            />
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={close}>
              {t("general:close")}
            </Button>
          </DialogActions>
        </>
      ),
      {
        dialogProps: {
          fullScreen: isMobile,
          sx: { maxHeight: { xs: "100%", md: "60vh" } },
        },
      },
    );
  }, [berth?.name, berthId, date, isMobile, orders, show, t]);

  return (
    <Box
      sx={{
        position: "relative",
        margin: "5px",
        width: "140px",
        height: "105px",
        /* border: "1px solid #E1E1E1", */
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: "#EFEFEF",
        boxShadow: 2,
        cursor: "pointer",
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          background: `linear-gradient(90deg, ${scaleGreenToRed(
            100 - (timeBlocksUsedInADay() / 48) * 100,
          )} 0%, rgba(70, 255, 39, 0) 110.88%)`,
          display: "flex",
          alignItems: "center",
          padding: (theme) => theme.spacing(0.6),
        }}
      >
        <Typography variant="body2" component="span">
          {t("total-orders", { orders: orders?.length ?? 0 })}
        </Typography>
        <Typography variant="body2" component="span" ml="auto">
          {`${usagePercentage}%`}
        </Typography>
      </Box>
      <Typography variant="body2" ml="5px">
        {t("time-left", {
          time: freeTimeLeft,
        })}
      </Typography>
      <AreaChart
        style={{ position: "absolute", bottom: -1, cursor: "pointer" }}
        width={140}
        height={52}
        data={graphData}
        margin={{ top: 0, left: 0, bottom: 0, right: 0 }}
      >
        <Area
          type="step"
          dataKey="value"
          fill="#00C2FF"
          fillOpacity="12%"
          stroke="#0075FF"
          strokeOpacity="50%"
        />
      </AreaChart>
    </Box>
  );
};
